import { useTheme } from "@amx/ux-twoui-theming";
import { Stack as FluentStack } from "@fluentui/react";
import React from "react";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { isMobileViewport, useViewport } from "../../../../styles";
import { TextAlignment } from "../../../../styles/fonts";
import { MediaProps } from "../../../Media/Media.types";
import { Stack } from "../../../Stacks/Stack/Stack";
import { ChoiceGroupOptionBaseProps } from "../../shared/props";
import { ChoiceGroupBaseProps } from "../../shared/props";
import { getButtonText, getSelectButtonLineItems } from "../../shared/utils";
import { getSelectButtonLineItemsHorizontalAlign, getSelectButtonStyles } from "./SelectButtonChoiceGroup.styles";

export interface SelectButtonChoiceGroupProps extends ChoiceGroupBaseProps {
    readonly optionType: "selectButton";

    readonly options: readonly SelectButtonChoiceGroupOption[];
}

export interface SelectButtonChoiceGroupOption extends ChoiceGroupOptionBaseProps {

    /** The representation for each line of text in the select button */
    readonly lines?: readonly LineItemChoices[];

    /** Text to be displayed in top right corner, only displayed when textAlignment is set to `left` for selectButtons and when mediaPosition is `left` for mediaButtons */
    readonly topRightText?: LineItem;

    /** Subtitle text displayed directly under the title text with media */
    readonly subTextWithMedia?: MediaLineItem;
}

interface BaseLineItem {
    //Text to display
    readonly text: string;

    readonly isBold?: boolean;
}

//Each line of text within Select Button
export interface LineItem extends BaseLineItem {
    readonly lineItemType?: "text";
}

export interface MediaLineItem extends Omit<MediaProps, "padding">, BaseLineItem {
    readonly lineItemType: "media";
}

export type LineItemChoices = LineItem | MediaLineItem;

export interface SelectButtonProps extends SelectButtonChoiceGroupOption, PickedSelectButtonChoiceGroup {
    /**
     * The text alignment for the text, it is overwriting the ChoiceGroupBaseProps of the same name as
     * required because even if users do not provide a value for textAlignment at the JSX level in ChoiceGroup.tsx
     * we are providing them with a default value of 'left'
     */
    readonly textAlignment: TextAlignment;
}

//Picking out the props that we care about from SelectButtonChoiceGroupProps
type PickedSelectButtonChoiceGroup = Pick<SelectButtonChoiceGroupProps, "height" | "textAlignment" | "optionType">;

export const buildTextAlignmentAndTopRightTextValidator = (textAlignment: TextAlignment, topRightText?: LineItem): ComponentValidator => (
    () => (topRightText && textAlignment !== "left" ? "SelectButtonChoiceGroup must have textAlignment set to `left` when provided a topRightText" : undefined)
);

export const SelectButton: React.FC<SelectButtonProps> = ({ text, lines, disabled, height, textAlignment, selected, topRightText, optionType, subTextWithMedia }) => {
    const viewportType = useViewport();
    const theme = useTheme();
    const isMobile = isMobileViewport(viewportType);

    validateComponent("SelectButtonChoiceGroup", [buildTextAlignmentAndTopRightTextValidator(textAlignment, topRightText)]);

    return (
        //Using Stack from Fluent instead of TwoUI because we can pass styles to stack in fluent but not the one in
        //TwoUI
        <FluentStack styles={getSelectButtonStyles(theme, isMobile, height, textAlignment, disabled, selected)}>
            {getButtonText(text, disabled, topRightText, optionType, subTextWithMedia)}

            <Stack horizontalAlign={getSelectButtonLineItemsHorizontalAlign(textAlignment)}>
                {getSelectButtonLineItems(lines, disabled)}
            </Stack>

        </FluentStack>
    );
};
